import React, { useEffect, useState, useMemo } from "react";
import { Typography, Select, Tag, Divider } from "antd";
import { getSingleCourseReport } from "../../../service/ReportingService.js";
import { useParams, useNavigate } from "react-router-dom";
import AdminHOC from "../../../components/shared/HOC/AdminHOC.jsx";
import { DownloadOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import CustomInput from "../../../components/shared/Input/Custom-Input.jsx";
import LottieIcon from "../../../components/shared/lottieIcon/LottieIcon.jsx";
import searchIconsvg from "../../../assets/icons/Search-Icon.svg";
import CustomButton from "../../../components/shared/button/CustomButton.jsx";
import CustomLabelDropdown from "../../../components/shared/Input/Custom-Label-DropDown.jsx";
import CommonTable from "../../../components/shared/table/Table.jsx";
import CustomBreadcrumb from "../../../components/shared/breadcrumbs/CustomBreadCrumb.jsx";
import Back from "../../../assets/back-btn.svg";
import {Card} from "antd";

const { Title, Text } = Typography;
const { Option } = Select;

const enrollmentSourceMap = {
  INDIVIDUAL: "Individual Assignment",
  BUNDLE: "Bundle Assignment",
};

const STATUS_OPTIONS = ["Completed", "In Progress", "Not Started"];

const formatDate = (val) => (val ? new Date(val).toLocaleDateString() : "-");

const SingleCourseReport = ({ setBreadCrumbs }) => {
  const { courseId } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [filterType, setFilterType] = useState("ALL");
  const [subType, setSubType] = useState("ALL");
  const [selectedGroup, setSelectedGroup] = useState("ALL");
  const [groupOptions, setGroupOptions] = useState([]);
  const [selectedBundle, setSelectedBundle] = useState(null);
  const [bundleOptions, setBundleOptions] = useState([]);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [searchText, setSearchText] = useState("");
  const [expandedRowKey, setExpandedRowKey] = useState(null);
  const [selectedUser, setSelectedUser] = useState("ALL");
  const [isSearchHovered, setIsSearchHovered] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const courseData = await getSingleCourseReport(courseId);
      setData({
        ...courseData,
        enrollments: courseData?.enrolledUsers || [],
      });

      const groupSet = new Set();
      const bundleSet = new Set();

      courseData?.enrolledUsers?.forEach((e) => {
        if (e.groupName && e.groupName !== "null") groupSet.add(e.groupName);
        if (e.bundleName && e.bundleName !== "null") bundleSet.add(e.bundleName);
      });

      setGroupOptions([...groupSet]);
      setBundleOptions([...bundleSet]);
    };
    fetchData();
  }, [courseId]);


  const userOptions = useMemo(() => {
    if (!data) return [];
    const users = data.enrollments
      .filter((e) => !e.groupId)
      .map((e) => e.fullName)
      .filter(Boolean);
    return [...new Set(users)].sort();
  }, [data]);

  const filteredEnrollments = () => {
    if (!data) return [];

    return data.enrollments.filter((e) => {
      const isIndividual = ["INDIVIDUAL", "BUNDLE"].includes(e.enrollmentSource);
      const isBundle = ["BUNDLE", "GROUP_BUNDLE"].includes(e.enrollmentSource);
      const isUser = !e.groupId;
      const isGroup = !!e.groupId;

      if (filterType === "BUNDLE" && !isBundle) return false;
      if (filterType === "INDIVIDUAL" && !isIndividual) return false;
      if (subType === "USER" && !isUser) return false;
      if (subType === "GROUP" && !isGroup) return false;
      if (subType === "GROUP" && selectedGroup !== "ALL" && e.groupName !== selectedGroup) return false;
      if (subType === "USER" && selectedUser !== "ALL" && e.fullName !== selectedUser) return false;
      if (selectedBundle && e.bundleName !== selectedBundle) return false;
      if (
        searchText &&
        !(
          (e.fullName && e.fullName.toLowerCase().includes(searchText.toLowerCase())) ||
          (e.groupName && e.groupName.toLowerCase().includes(searchText.toLowerCase())) ||
          (e.bundleName && e.bundleName.toLowerCase().includes(searchText.toLowerCase()))
        )
      )
        return false;

      return true;
    });
  };

  const formatPercentage = (val) => {
    const num = Number(val || 0);
    return `${num % 1 === 0 ? num : num.toFixed(2)}%`;
  };

  const renderStatusTag = (status) => {
    let color, background, borderColor;

    if (status === "Completed") {
      color = "#066710";
      background = "#EEFFF0";
      borderColor = "#95DE9D";
    } else if (status === "In Progress") {
      color = "#C77A00";
      background = "#FFF8C7";
      borderColor = "#FBDC94";
    } else {
      color = "#C80000";
      background = "#FFEEEE";
      borderColor = "#FFAAAA";
    }

    return (
      <Tag
        style={{
          padding: "10px 16px",
          borderRadius: "10px",
          border: "1px solid",
          color: color,
          fontWeight: "500",
          borderColor: borderColor,
          backgroundColor: background,
        }}
        color={color}
      >
        {status}
      </Tag>
    );
  };

  const getCommonColumns = () => [
    {
      title: "Enrolled At",
      dataIndex: "enrolledAt",
      render: formatDate,
      sorter: (a, b) => new Date(a.enrolledAt) - new Date(b.enrolledAt),
    },
    {
      title: "Deadline",
      dataIndex: "deadline",
      render: formatDate,
      sorter: (a, b) => new Date(a.deadline) - new Date(b.deadline),
    },
    {
      title: "Progress",
      dataIndex: "courseCompletionPercentage",
      render: formatPercentage,
      sorter: (a, b) => a.courseCompletionPercentage - b.courseCompletionPercentage,
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status) => renderStatusTag(status),
    },
    {
      title: "Adherence",
      dataIndex: "adherence",
      sorter: (a, b) => (a.adherence || "").localeCompare(b.adherence || ""),
    },
  ];

  const enrolleeColumns = [
    {
      title: "Name",
      dataIndex: "displayTitle",
      sorter: (a, b) => a.displayTitle.localeCompare(b.displayTitle),
      render: (_, record) => (
        <>
          <strong>{record.displayTitle}</strong>
          <div style={{ fontSize: "12px", color: "#888" }}>
            Enrollment: {enrollmentSourceMap[record.enrollmentSource] || record.enrollmentSource}
            {record.groupName && record.groupName !== "null" && record.enrolleeType === "USER" && (
              <div>Group: {record.groupName}</div>
            )}
            {record.bundleName && record.bundleName !== "null" && (
              <div>Bundle: {record.bundleName}</div>
            )}
          </div>
        </>
      ),
    },
    {
      title: "Type",
      dataIndex: "enrolleeType",
      filters: [
        { text: "User", value: "USER" },
        { text: "Group", value: "GROUP" },
      ],
      onFilter: (value, record) => record.enrolleeType === value,
      render: (val) => (val === "USER" ? "User" : "Group"),
    },
    ...getCommonColumns(),
  ];

  const handleExport = () => {
    const currentData = expandedRowKey
      ? topLevelEnrollments.filter((e) => e.enrollmentId === expandedRowKey)
      : topLevelEnrollments;

    const exportData = currentData.map((row) => {
      const formattedRow = {};
      enrolleeColumns.forEach((col) => {
        const key = col.dataIndex;
        const title = col.title;
        let value = row[key];

        if (key === "status") {
          const statusMap = {
            COMPLETED: "Completed",
            IN_PROGRESS: "In Progress",
            PENDING: "Pending",
          };
          value = statusMap[value] || value;
        } else if (col.render && typeof col.render === "function") {
          const rendered = col.render(value, row);
          value = typeof rendered === "string" ? rendered : String(value);
        }

        formattedRow[title] = typeof value === "string" ? value : String(value);
      });

      return formattedRow;
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Course Report");
    XLSX.writeFile(wb, `${data.courseTitle}_Enrollees.xlsx`);
  };

  if (!data || data.enrollments.length === 0) {
    return (
      <div style={{ padding: "0px 16px" }}>
        <Text>No enrollments found.</Text>
      </div>
    );
  }

  const groupEnrollments = data.enrollments.reduce((acc, e) => {
    if (e.groupId) {
      if (!acc[e.groupId]) acc[e.groupId] = [];
      acc[e.groupId].push(e);
    }
    return acc;
  }, {});

  const topLevelEnrollments = filteredEnrollments()
    .filter((e, idx, arr) => {
      if (e.groupId) {
        return arr.findIndex((x) => x.groupId === e.groupId) === idx;
      }
      return true;
    })
    .map((e) => {
      if (e.groupId) {
        const users = groupEnrollments[e.groupId] || [];

        const progressValues = users.map((u) => u.courseCompletionPercentage || 0);
        const avgProgress = progressValues.length
          ? (() => {
              const avg = progressValues.reduce((a, b) => a + b, 0) / progressValues.length;
              return avg % 1 === 0 ? avg : Number(avg.toFixed(2));
            })()
          : 0;

        const statuses = users.map((u) => u.status);
        const isCompleted = statuses.every((s) => s === "Completed");
        const groupStatus = isCompleted
          ? "Completed"
          : statuses.includes("In Progress")
          ? "In Progress"
          : "Not Started";

        const adherenceOrder = [
          "Overdue",
          "Not Due Yet",
          "Behind Schedule",
          "On Track",
          "Late",
          "On Time",
        ];
        const worstAdherence = users.reduce((worst, u) => {
          const currentIndex = adherenceOrder.indexOf(u.adherence || "Completed");
          const worstIndex = adherenceOrder.indexOf(worst || "Completed");
          return currentIndex > worstIndex ? u.adherence : worst;
        }, "Completed");

        return {
          ...e,
          displayTitle: e.groupName,
          enrolleeType: "GROUP",
          courseCompletionPercentage: avgProgress,
          status: groupStatus,
          adherence: worstAdherence,
          enrolledAt: e.assignedAt,
        };
      }

      return {
        ...e,
        displayTitle: e.fullName,
        enrolleeType: "USER",
        enrolledAt: e.assignedAt,
      };
    })
    .filter((e) => {
      if (statusFilter !== "ALL") {
        return e.status === statusFilter;
      }
      return true;
    });

  const renderExpandedRow = (record) => {
    if (record.enrolleeType === "GROUP") {
      const groupUsers = groupEnrollments[record.groupId] || [];

      return groupUsers.length > 0 ? (
        <CommonTable
          data={groupUsers.map((u) => ({
            ...u,
            displayTitle: u.fullName,
            enrolleeType: "USER",
            enrolledAt: u.assignedAt,
          }))}
          columns={enrolleeColumns}
          pagination={false}
          serverSide={false}
        />
      ) : (
        <Text type="secondary">No users in this group.</Text>
      );
    }
    return null;
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <Title
          style={{
            marginBottom: 0,
            color: "rgb(231, 72, 96)",
            fontWeight: 500,
            fontSize: 28,
          }}
        >
          {data.courseTitle}
        </Title>

        <div style={{ display: "flex", gap: 32 }}>
          <Text style={{ fontSize: 16, color: "#444" }}>
            <span style={{ fontWeight: 600 }}>Total Enrollments:</span> {data.enrollments.length}
          </Text>
        </div>
      </div>

      <div
  style={{
    display: "flex",
    gap: 10,
    alignItems: "center",
    marginBottom: 24,
    flexWrap: "wrap",
  }}
>

  <div
    onMouseEnter={() => setIsSearchHovered(true)}
    onMouseLeave={() => setIsSearchHovered(false)}
  >
    <CustomInput
      icon={
        isSearchHovered ? (
          <LottieIcon name="searchIcon" size={20} shouldPlay={true} />
        ) : (
          <img src={searchIconsvg} alt="search" style={{ width: 20, height: 20 }} />
        )
      }
      style={{ width: 260 }}
      value={searchText}
      onChange={(e) => setSearchText(e.target.value)}
      placeholder="Search user, courses or curriculums"
      allowClear
    />
  </div>

  <div style={{ display: "flex", gap: 10, alignItems: "center", marginLeft: "auto", flexWrap: "wrap" }}>
    <CustomLabelDropdown
      value={filterType}
      onChange={(v) => {
        setFilterType(v);
        setSelectedBundle(null);
      }}
      options={[
        { value: "ALL", label: "All Enrollments" },
        { value: "INDIVIDUAL", label: "Individual" },
        { value: "BUNDLE", label: "Bundle" },
      ]}
      placeholder="All Enrollments"
      wrapperClass="filter-type-selector"
    />

    {filterType === "BUNDLE" && bundleOptions.length > 0 && (
      <CustomLabelDropdown
        value={selectedBundle}
        onChange={setSelectedBundle}
        options={[
          { value: null, label: "All Bundles" },
          ...bundleOptions.map((b) => ({ value: b, label: b })),
        ]}
        placeholder="Select bundle"
        wrapperClass="bundle-selector"
      />
    )}

    <CustomLabelDropdown
      value={subType}
      onChange={(v) => {
        setSubType(v);
        setSelectedGroup("ALL");
      }}
      options={[
        { value: "ALL", label: "All Types" },
        { value: "USER", label: "Users" },
        { value: "GROUP", label: "Groups" },
      ]}
      placeholder="All Types"
      wrapperClass="subtype-selector"
    />

    {subType === "GROUP" && (
      <CustomLabelDropdown
        value={selectedGroup}
        onChange={setSelectedGroup}
        options={[
          { value: "ALL", label: "All Groups" },
          ...groupOptions.map((group) => ({ value: group, label: group })),
        ]}
        placeholder="Select Group"
        wrapperClass="group-selector"
      />
    )}

    {subType === "USER" && (
      <CustomLabelDropdown
        value={selectedUser}
        onChange={setSelectedUser}
        options={[
          { value: "ALL", label: "All Users" },
          ...userOptions.map((user) => ({ value: user, label: user })),
        ]}
        placeholder="Select User"
        showSearch={true}
        wrapperClass="user-selector"
      />
    )}

    <CustomLabelDropdown
      value={statusFilter}
      onChange={setStatusFilter}
      options={[
        { value: "ALL", label: "All Status" },
        ...STATUS_OPTIONS.map((s) => ({ value: s, label: s })),
      ]}
      placeholder="All Status"
      wrapperClass="status-filter"
    />

    <CustomButton
      icon={<DownloadOutlined />}
      onClick={handleExport}
      className="add-btn"
    >
      Export
    </CustomButton>

    <CustomButton
      icon={<QuestionCircleOutlined />}
      onClick={() => {
        navigate(
          `/quiz-report?courseId=${courseId}&courseName=${encodeURIComponent(data.courseTitle)}`
        );
      }}
      className="add-btn"
    >
      Quiz Reports
    </CustomButton>
  </div>
</div>
      {topLevelEnrollments.length === 0 ? (
        <Text style={{ display: "block", textAlign: "center", marginTop: 24 }}>
          No enrollments match the selected filters
        </Text>
      ) : (
        <CommonTable
          data={
            expandedRowKey
              ? topLevelEnrollments.filter((e) =>
                  e.groupId
                    ? `group-${e.groupId}` === expandedRowKey
                    : e.userId === expandedRowKey
                )
              : topLevelEnrollments
          }
          columns={enrolleeColumns}
          serverSide={false}
          pagination={{ pageSize: 10 }}
          expandedRowRender={renderExpandedRow}
          rowExpandable={(record) => record.enrolleeType === "GROUP" && !!record.groupId}
          onExpand={(expanded, record) =>
            setExpandedRowKey(
              expanded
                ? record.groupId
                  ? `group-${record.groupId}`
                  : record.userId
                : null
            )
          }
        />
      )}
    
    </div>
  );
};

export default AdminHOC(SingleCourseReport);